import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDays,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowLeft,
  Inbox
} from 'lucide-react';
import { getMyAttendanceRequests } from '../../services/employeeService';
import './AttendanceRequests.css';
import '../Employee/LeaveList.css'; // Reuse shared EMS styles

const AttendanceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await getMyAttendanceRequests();
        setRequests(data);
      } catch (err) {
        console.error("Failed to fetch attendance requests", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const getStatusStyles = (status) => {
    switch (status) {
      case 'approved':
        return {
          bg: 'ems-status-approved',
          icon: <CheckCircle2 size={14} />
        };
      case 'rejected':
        return {
          bg: 'ems-status-declined',
          icon: <XCircle size={14} />
        };
      default:
        return {
          bg: 'ems-status-pending',
          icon: <Clock size={14} />
        };
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const filtered = filter === 'all'
    ? requests
    : requests.filter(r => r.changeRequest?.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.changeRequest?.status === 'pending').length,
    approved: requests.filter(r => r.changeRequest?.status === 'approved').length,
    rejected: requests.filter(r => r.changeRequest?.status === 'rejected').length,
  };

  return (
    <div className="ems-page-container">
      <div className="ems-max-width">

        {/* Back link */}
        <Link to="/employee/dashboard" className="att-back-link">
          <ArrowLeft size={16} />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="ems-header-section">
          <div>
            <h1 className="ems-page-title">Attendance Requests</h1>
            <p className="ems-page-subtitle">All your attendance change requests and their status.</p>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="att-filter-tabs" style={{ marginBottom: '1.5rem' }}>
          {['all', 'pending', 'approved', 'rejected'].map(tab => (
            <button
              key={tab}
              className={`att-filter-tab ${filter === tab ? 'active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span className="att-filter-count">{counts[tab]}</span>
            </button>
          ))}
        </div>

        {/* Loading */}
        {loading ? (
          <div className="ems-loading-skeleton">
            {[1, 2, 3].map(i => <div key={i} className="ems-skeleton-card" />)}
          </div>
        ) : filtered.length === 0 ? (
          /* Empty State */
          <div className="ems-empty-state">
            <div className="ems-empty-icon">
              <Inbox size={40} />
            </div>
            <h3 className="ems-empty-title">No Requests Found</h3>
            <p className="ems-empty-subtitle">
              {filter === 'all'
                ? "You haven't submitted any attendance change requests yet."
                : `No ${filter} requests found.`}
            </p>
          </div>
        ) : (
          /* Request Cards */
          <div className="att-requests-grid">
            {filtered.map((req) => {
              const status = req.changeRequest?.status || 'pending';
              const styles = getStatusStyles(status);
              return (
                <div key={req._id} className="att-request-card">
                  <div className="att-request-content">
                    <div className="att-request-info">
                      {/* Date */}
                      <div className="att-request-date-row">
                        <div className="att-date-icon">
                          <CalendarDays size={18} />
                        </div>
                        <span className="att-date-text">
                          {formatDate(req.date)}
                        </span>
                      </div>

                      {/* Time Details */}
                      <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '1rem',
                          marginTop: '0.5rem',
                          backgroundColor: '#f8fafc',
                          padding: '1rem',
                          borderRadius: '0.75rem',
                          border: '1px solid #e2e8f0'
                      }}>
                        <div className="att-time-column">
                          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>ORIGINAL TIME</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#334155', fontWeight: '500' }}>
                                <span style={{ color: '#94a3b8', width: '30px', display: 'inline-block' }}>In:</span> 
                                {formatTime(req.changeRequest?.originalLogin || req.loginTime)}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#334155', fontWeight: '500' }}>
                                <span style={{ color: '#94a3b8', width: '30px', display: 'inline-block' }}>Out:</span> 
                                {formatTime(req.changeRequest?.originalLogout || req.logoutTime)}
                            </div>
                          </div>
                        </div>
                        
                        <div className="att-time-column">
                          <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#1E88E5', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>
                            {status === 'approved' ? 'APPROVED TIME' : 'REQUESTED TIME'}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#0f172a', fontWeight: '600' }}>
                                <span style={{ color: '#94a3b8', fontWeight: 'normal', width: '30px', display: 'inline-block' }}>In:</span> 
                                {formatTime(status === 'approved' ? req.loginTime : req.changeRequest?.requestedLogin)}
                            </div>
                            <div style={{ fontSize: '0.875rem', color: '#0f172a', fontWeight: '600' }}>
                                <span style={{ color: '#94a3b8', fontWeight: 'normal', width: '30px', display: 'inline-block' }}>Out:</span> 
                                {formatTime(status === 'approved' ? req.logoutTime : req.changeRequest?.requestedLogout)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Reason */}
                      {req.changeRequest?.reason && (
                        <p className="att-request-reason">
                          Reason: {req.changeRequest.reason}
                        </p>
                      )}
                    </div>

                    {/* Status */}
                    <div className="att-request-status-section">
                      <span className={`ems-status-badge ${styles.bg}`}>
                        {styles.icon}
                        {status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceRequests;
